'use client';

import * as React from 'react';
import { useStableCallback } from '@base-ui/utils/useStableCallback';
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
import { buildIsValidDropTarget } from "../../build-is-valid-drop-target/index.mjs";
import { useDropTarget } from "../../internals/utils/useDropTarget.mjs";
import { EVENT_DRAG_PRECISION_MINUTE, EVENT_DRAG_PRECISION_MS } from "../../constants/index.mjs";
import { schedulerEventSelectors } from "../../scheduler-selectors/index.mjs";
import { useEventCalendarStoreContext } from "../../use-event-calendar-store-context/index.mjs";
const isValidDropTarget = buildIsValidDropTarget(['CalendarGridTimeEvent', 'CalendarGridTimeEventResizeHandler', 'CalendarGridDayEvent', 'StandaloneEvent']);
export function useTimeDropTarget(parameters) {
  const {
    start,
    end,
    addPropertiesToDroppedEvent
  } = parameters;

  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();

  // Ref hooks
  const ref = React.useRef(null);
  const collectionStartTimestamp = adapter.getTime(start);
  const collectionEndTimestamp = adapter.getTime(end);
  const collectionDurationMs = collectionEndTimestamp - collectionStartTimestamp;
  const getCursorPositionInElementMs = useStableCallback(({
    input,
    elementRef
  }) => {
    if (!ref.current || !elementRef.current) {
      return 0;
    }
    const clientY = input.clientY;
    const elementPosition = elementRef.current.getBoundingClientRect();
    const positionY = (clientY - elementPosition.y) / ref.current.offsetHeight;
    const clampedPositionY = Math.max(0, Math.min(1, positionY));
    return Math.round(collectionDurationMs * clampedPositionY);
  });
  const getEventDropData = useStableCallback(({
    data,
    getDataFromInside,
    getDataFromOutside,
    input
  }) => {
    if (!isValidDropTarget(data)) {
      return undefined;
    }
    const cursorOffsetMs = getCursorPositionInElementMs({
      input,
      elementRef: ref
    });
    const addOffsetToDate = (date, offsetMs) => {
      const roundedOffset = Math.round(offsetMs / EVENT_DRAG_PRECISION_MS) * EVENT_DRAG_PRECISION_MS;
      return adapter.addMilliseconds(date, roundedOffset);
    };

    // Move a Time Grid Event within the Time Grid
    if (data.source === 'CalendarGridTimeEvent') {
      const eventDurationMs = adapter.getTime(data.end) - adapter.getTime(data.start);
      let newStartDate = addOffsetToDate(start, cursorOffsetMs - data.initialCursorPositionInEventMs);

      // Clamp the event to stay within the time grid bounds
      if (adapter.isBefore(newStartDate, start)) {
        newStartDate = start;
      }
      const maxStartDate = adapter.addMilliseconds(end, -eventDurationMs);
      if (adapter.isAfter(newStartDate, maxStartDate)) {
        newStartDate = maxStartDate;
      }
      const newEndDate = adapter.addMilliseconds(newStartDate, eventDurationMs);
      return getDataFromInside(data, newStartDate, newEndDate);
    }

    // Resize a Time Grid Event
    if (data.source === 'CalendarGridTimeEventResizeHandler') {
      if (data.side === 'start') {
        let cursorDate = addOffsetToDate(start, cursorOffsetMs - data.initialCursorPositionInEventMs);

        // Clamp to the time grid bounds
        if (adapter.isBefore(cursorDate, start)) {
          cursorDate = start;
        }

        // Ensure the new start date is not after or too close to the end date.
        const maxStartDate = adapter.addMinutes(data.end, -EVENT_DRAG_PRECISION_MINUTE);
        const newStartDate = adapter.isBefore(cursorDate, maxStartDate) ? cursorDate : maxStartDate;
        return getDataFromInside(data, newStartDate, data.end);
      }
      if (data.side === 'end') {
        const eventDurationMs = adapter.getTime(data.end) - adapter.getTime(data.start);
        let cursorDate = addOffsetToDate(start, cursorOffsetMs - data.initialCursorPositionInEventMs + eventDurationMs);

        // Clamp to the time grid bounds
        if (adapter.isAfter(cursorDate, end)) {
          cursorDate = end;
        }

        // Ensure the new end date is not before or too close to the start date.
        const minEndDate = adapter.addMinutes(data.start, EVENT_DRAG_PRECISION_MINUTE);
        const newEndDate = adapter.isAfter(cursorDate, minEndDate) ? cursorDate : minEndDate;
        return getDataFromInside(data, data.start, newEndDate);
      }
    }

    // Move a Day Grid Event into the Time Grid
    if (data.source === 'CalendarGridDayEvent') {
      const newStartDate = addOffsetToDate(start, cursorOffsetMs);
      const newEndDate = adapter.addMinutes(newStartDate, schedulerEventSelectors.defaultEventDuration(store.state));
      return getDataFromInside(data, newStartDate, newEndDate);
    }

    // Move a Standalone Event into the Time Grid
    if (data.source === 'StandaloneEvent') {
      return getDataFromOutside(data, addOffsetToDate(start, cursorOffsetMs));
    }
    return undefined;
  });
  useDropTarget({
    ref,
    surfaceType: 'time-grid',
    getEventDropData,
    isValidDropTarget,
    addPropertiesToDroppedEvent
  });
  return {
    getCursorPositionInElementMs,
    ref
  };
}